import axios from 'axios';
import { TradeType } from '../types';
const getLiquidityType = (pool) => (pool.includes('/') ? 'Savers' : 'LP');
const getSwapType = (memo) => {
    const streamingSwapRegex = /:(\d+|(\d+(?:e[+-]?\d+)?))\/\d+\/\d+:/;
    return streamingSwapRegex.test(memo) ? 'Streaming' : 'Standard';
};
export const getAffiliateName = (memo) => {
    const [action] = memo.split(':');
    switch (action.toLowerCase()) {
        case 'swap':
        case '=':
        case 's': {
            // SWAP:ASSET:DESTADDR:LIM/INTERVAL/QUANTITY:AFFILIATE:FEE
            const [, , , , affiliateName] = memo.split(':');
            return affiliateName;
        }
        case 'add':
        case '+':
        case 'a': {
            // ADD:POOL:PAIRADDRESS:AFFILIATE:FEE
            const [, , , affilateName] = memo.split(':');
            return affilateName;
        }
        case '$+':
        case 'loan+': {
            // LOAN+:ASSET:DESTADDR:MINOUT:AFFILIATE:FEE
            const [, , , , affiliateName] = memo.split(':');
            return affiliateName;
        }
        case 'pool-': {
            // POOL-:BASISPOINTS:AFFILIATE:FEE
            const [, , affilateName] = memo.split(':');
            return affilateName;
        }
        default:
            return '';
    }
};
// Common parser for Thorchain and Mayachain
export class Parser {
    axiosMidgard;
    dexName;
    parserName;
    constructor(args) {
        this.axiosMidgard = axios.create({ baseURL: args.midgardUrl });
        this.dexName = args.dexName;
        this.parserName = args.parserName;
    }
    async _parse(memo) {
        const [type] = memo.split(':');
        switch (type.toLowerCase()) {
            case 'swap':
            case '=':
            case 's': {
                return {
                    data: {
                        parser: this.parserName,
                        method: 'swap',
                        memo,
                        swap: { type: getSwapType(memo) },
                    },
                    trade: { dexName: this.dexName, type: TradeType.Swap, memo },
                };
            }
            case 'add':
            case 'a':
            case '+': {
                const [, pool] = memo.split(':');
                const type = getLiquidityType(pool);
                return { data: { parser: this.parserName, method: 'deposit', memo, liquidity: { type } } };
            }
            case 'pool+': {
                const type = 'RUNEPool';
                return { data: { parser: this.parserName, method: 'deposit', memo, liquidity: { type } } };
            }
            case 'withdraw':
            case '-':
            case 'wd': {
                const [, pool] = memo.split(':');
                const type = getLiquidityType(pool);
                return { data: { parser: this.parserName, method: 'withdraw', memo, liquidity: { type } } };
            }
            case 'pool-': {
                const type = 'RUNEPool';
                return {
                    data: { parser: this.parserName, method: 'withdrawNative', memo, liquidity: { type } },
                };
            }
            case '$+':
            case 'loan+':
                return { data: { parser: this.parserName, memo, method: 'loanOpen' } };
            case '$-':
            case 'loan-':
                return { data: { parser: this.parserName, memo, method: 'loanRepayment' } };
            case 'tcy':
                return { data: { parser: this.parserName, memo, method: 'claim' } };
            case 'tcy+':
                return { data: { parser: this.parserName, memo, method: 'stake' } };
            case 'tcy-':
                return { data: { parser: this.parserName, memo, method: 'unstake' } };
            case 'out': {
                const extraMetadata = await this.getExtraMetadata(memo);
                if (!extraMetadata)
                    return;
                return { data: { parser: this.parserName, memo, ...extraMetadata } };
            }
            case 'refund':
                const extraMetadata = await this.getExtraMetadata(memo);
                if (!extraMetadata)
                    return;
                return { data: { parser: this.parserName, memo, ...extraMetadata } };
            default:
                return;
        }
    }
    getExtraMetadata = async (memo) => {
        const [type, txid] = memo.split(':');
        const { data } = await this.axiosMidgard.get(`/actions?txid=${txid}`);
        const action = data.actions[data.actions.length - 1];
        if (!action)
            return;
        const swapMemo = action.metadata?.swap?.memo ?? '';
        const [swapType] = swapMemo.split(':');
        const refundMemo = action.metadata?.refund?.memo ?? '';
        const [refundType] = refundMemo.split(':');
        const txType = (() => {
            if (swapType) {
                switch (swapType.toLowerCase()) {
                    case '$+':
                    case 'loan+':
                        return 'loanOpen';
                    case '$-':
                    case 'loan-':
                        return 'loanRepayment';
                    default:
                        return action.type;
                }
            }
            if (refundType) {
                switch (refundType.toLowerCase()) {
                    case 'swap':
                    case '=':
                    case 's':
                        return 'swap';
                    case '$+':
                    case 'loan+':
                        return 'loanOpen';
                    case '$-':
                    case 'loan-':
                        return 'loanRepayment';
                    case 'add':
                    case '+':
                        return 'deposit';
                    case 'withdraw':
                    case '-':
                    case 'wd':
                        return 'withdraw';
                    default:
                        return action.type;
                }
            }
            return action.type;
        })();
        const method = (() => {
            switch (type.toLowerCase()) {
                case 'out': {
                    return txType ? `${txType}Out` : 'out';
                }
                case 'refund': {
                    return txType ? `${txType}Refund` : 'refund';
                }
                default:
                    return 'unknown';
            }
        })();
        const liquidity = (() => {
            if (type.toLowerCase() === 'refund' && ['withdraw', 'deposit'].includes(txType))
                return { type: getLiquidityType(refundMemo.split(':')[1]) };
            if (txType === 'withdraw')
                return { type: getLiquidityType(action.pools[0]) };
        })();
        const swap = txType === 'swap' ? { type: getSwapType(swapMemo || refundMemo) } : undefined;
        const originMemo = action.metadata?.swap?.memo ||
            action.metadata?.refund?.memo ||
            action.metadata?.withdraw?.memo;
        return { method, liquidity, swap, originMemo };
    };
}
